import type { z } from "zod";
import { localProductSchema } from "../schemas/localProductSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@chakra-ui/react";
import type { LocalProduct } from "../types/LocalProduct";
import LocalProductForm from "./LocalProductForm";
import { useUpdateLocalProduct } from "../useCases/useUpdateLocalProduct";
import { useAuth } from "@/shared/session";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { toast } from "@/ui/chakra/toaster";

type LocalProductFormInput = z.infer<typeof localProductSchema>;

interface CreateLocalProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onError?: (error: {
    relatedVouchers: {
      voucherCode: string;
      usedStock: number;
      productCode: string;
    }[];
  }) => void;
  selectedLocalProduct: LocalProduct;
}

export const EditLocalProductModal = ({
  isOpen,
  onClose,
  selectedLocalProduct,
  onError,
}: CreateLocalProductModalProps) => {
  const { mutate, isPending } = useUpdateLocalProduct();
  const { session } = useAuth();

  const { handleSubmit, control } = useForm<LocalProductFormInput>({
    resolver: zodResolver(localProductSchema),
    defaultValues: {
      code: selectedLocalProduct.code,
      comment: selectedLocalProduct.comment,
      purchasePrice: selectedLocalProduct.purchasePrice,
      maximumStock: selectedLocalProduct.maximumStock,
      description: selectedLocalProduct.product.description,
      unit: selectedLocalProduct.product.unit,
      location: selectedLocalProduct.product.location,
      stock:
        selectedLocalProduct.inventoryDetails.find(
          (detail) =>
            detail.replenishmentId === "" || detail.replenishmentId === null,
        )?.stock || 0,
      minimumStock: selectedLocalProduct.product.minimumStock,
      converter: selectedLocalProduct.product.converter,
      converterTypeId: selectedLocalProduct.product.converterTypeId,
      storageId: selectedLocalProduct.product.storageId,
      zoneId: session?.zone ?? "",
      images: selectedLocalProduct.imageUrl
        ? [selectedLocalProduct.imageUrl]
        : [],
    },
  });
  const onFormSubmit = (data: LocalProductFormInput) => {
    const imageFiles = (data.images ?? []).filter(
      (img): img is File => img instanceof File,
    );

    const payloadWithoutImages = {
      ...data,
      images: undefined,
    };

    mutate(
      {
        id: selectedLocalProduct.id,
        updateLocalProducts: payloadWithoutImages,
        imageFiles: imageFiles.length > 0 ? imageFiles : undefined,
      },
      {
        onError: (error) => {
          console.error("Error al actualizar el producto local:", error);
          onError && onError(error.response?.data?.error);
          onClose();
        },
        onSuccess: () => {
          toast({
            title: "Producto local actualizado correctamente",
            description: "El producto local ha sido actualizado correctamente",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
        },
      },
    );
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <DialogContent>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle>Editar producto local</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            <LocalProductForm control={control} />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} type="button">
              Cancelar
            </Button>
            <Button colorPalette="brand" type="submit" loading={isPending}>
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </DialogRoot>
  );
};
